import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { Section as SectionComponent } from "@sunwing/shared-components";
import {
	Heading,
	HeadingSize as SectionTitleSize,
	HeadingAsElement as SectionTitleAsElement,
} from "@sunwing/wvi-components";
import * as styles from "./Section.module.scss";

const SectionAsElement = {
	section: "section",
	header: "header",
	footer: "footer",
};

const SectionTheme = {
	light: "light",
	dark: "dark",
	darkTeal: "darkTeal",
	transparent: "transparent",
};

const SectionPadding = {
	none: "none",
	micro: "micro",
	extraSmall: "extraSmall",
	small: "small",
	medium: "medium",
	large: "large",
	extraLarge: "extraLarge",
};

const SectionPaddingControl = {
	removeTop: "removeTop",
	removeBottom: "removeBottom",
};

const SectionTitleAlign = {
	left: "left",
	center: "center",
};

const Section = ({
	className,
	classNames: providedClassNames,
	as,
	theme,
	padding,
	paddingControl,
	backgroundImage,
	titleId,
	title,
	titleAs,
	titleSize,
	titleAlignment,
	children,
}) => {
	const classNames = cx(
		styles.section,
		{
			[styles[theme]]: styles[theme],
			[styles[padding]]: styles[padding],
			[styles[paddingControl]]: styles[paddingControl],
			[styles.bgImg]: backgroundImage,
		},
		className
	);
	return (
		<SectionComponent
			className={classNames}
			classNames={{
				"section__background-image-container": styles.sectionBackgroundImageContainer,
				section__container: cx(styles.sectionContainer, providedClassNames),
				section__title: styles.sectionTitle,
				section__content: styles.sectionContent,
			}}
			as={as}
			theme={theme}
			padding={padding}
			backgroundImage={backgroundImage}
			TitleComponent={Heading}
			titleId={titleId}
			title={title}
			titleAs={titleAs}
			titleSize={titleSize}
			titleAlignment={titleAlignment}>
			{children}
		</SectionComponent>
	);
};

Section.displayName = "Section";

Section.propTypes = {
	className: PropTypes.string,
	classNames: PropTypes.string,
	/**
	 * Import SectionAsElement for list of `as` available
	 */
	as: PropTypes.oneOf(Object.values(SectionAsElement)),
	/**
	 * Import SectionTheme for list of `theme` available
	 */
	theme: PropTypes.oneOf(Object.values(SectionTheme)),
	/**
	 * Import SectionPadding for list of `padding` available
	 */
	padding: PropTypes.oneOf(Object.values(SectionPadding)),
	/**
	 * Import SectionPaddingControl for list of `paddingControl` available
	 */
	paddingControl: PropTypes.oneOf(Object.values(SectionPaddingControl)),
	backgroundImage: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
	titleId: PropTypes.string,
	title: PropTypes.node,
	/**
	 * Import SectionTitleAsElement for list of `titleAs` available
	 */
	titleAs: PropTypes.oneOf(Object.values(SectionTitleAsElement)),
	/**
	 * Import SectionTitleSize for list of `titleSize` available
	 */
	titleSize: PropTypes.oneOf(Object.values(SectionTitleSize)),
	/**
	 * Import SectionTitleAlign for list of `titleAlignment` available
	 */
	titleAlignment: PropTypes.oneOf(Object.values(SectionTitleAlign)),
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

Section.defaultProps = {
	className: undefined,
	classNames: undefined,
	as: SectionAsElement.section,
	theme: SectionTheme.transparent,
	padding: SectionPadding.medium,
	paddingControl: undefined,
	backgroundImage: undefined,
	titleId: undefined,
	title: undefined,
	titleAs: SectionTitleAsElement.h2,
	titleSize: SectionTitleSize.xl,
	titleAlignment: SectionTitleAlign.left,
	children: undefined,
};

export {
	Section,
	SectionAsElement,
	SectionTheme,
	SectionPadding,
	SectionPaddingControl,
	SectionTitleAsElement,
	SectionTitleSize,
	SectionTitleAlign,
};
